export const ACTION_LABELS = {
  view: 'Visualizar',
  create: 'Crear',
  edit: 'Editar',
  approve: 'Aprobar',
  reject: 'Rechazar',
  download: 'Descargar',
  delete: 'Eliminar',
  OFF: 'Sin acceso',
  READ: 'Solo ver',
  READ_WRITE: 'Todo editar',
  DOWNLOADS: 'Descargar'
}

const AUDIT_ACTION_LABELS = {
  READ_WRITE: 'todo editar',
  READ: 'solo ver',
  DOWNLOADS: 'descargar',
  DOWNLOAD: 'descargar',
  OFF: 'apagado'
}

const AUDIT_ACTION_PATTERN = /\b(READ_WRITE|READ|DOWNLOADS|DOWNLOAD|OFF)\b/g

export function formatRbacAuditSummary(summary) {
  if (!summary) return '—'

  return summary.replace(
    AUDIT_ACTION_PATTERN,
    (action) => AUDIT_ACTION_LABELS[action] || action
  )
}

export function permissionKey(moduleKey, scopeKey, action) {
  return `${moduleKey}.${scopeKey}:${action}`
}

export function hasPermission(permissions, moduleKey, scopeKey, action) {
  return permissions.includes(permissionKey(moduleKey, scopeKey, action))
}

export function flattenRolePermissions(permissionsByScope) {
  const flat = []
  Object.entries(permissionsByScope || {}).forEach(([key, actions]) => {
    const [moduleKey, scopeKey] = key.split('|')
    ;(actions || []).forEach((action) => {
      flat.push(permissionKey(moduleKey, scopeKey, action))
    })
  })
  return flat.sort()
}

export function permissionsToPayload(permissionsByScope) {
  return Object.entries(permissionsByScope || {})
    .filter(([, actions]) => actions && actions.length > 0)
    .map(([key, actions]) => {
      const [moduleKey, scopeKey] = key.split('|')
      return {
        module_key: moduleKey,
        scope_key: scopeKey,
        actions
      }
    })
}

export function rolePermissionsToMap(permissions = []) {
  const map = {}
  permissions.forEach(({ module_key, scope_key, actions }) => {
    map[`${module_key}|${scope_key}`] = [...(actions || [])]
  })
  return map
}

export function reconcilePermissionsWithCatalog(permissionsByScope, catalog) {
  const availableKeys = new Set()
  const modules = catalog?.modules || []

  modules.forEach((module) => {
    scopesForModule(module).forEach((scope) => {
      availableKeys.add(`${module.key}|${scope.key}`)
    })
  })

  const permissions = {}
  const removedKeys = []
  Object.entries(permissionsByScope || {}).forEach(([key, actions]) => {
    if (availableKeys.has(key)) {
      permissions[key] = [...(actions || [])]
    } else if ((actions || []).length > 0) {
      removedKeys.push(key)
    }
  })

  return { permissions, removedKeys }
}

export function applyPresetToScope(scopeActions, preset) {
  const available = (scopeActions || []).filter((action) => action !== 'OFF')
  const firstAvailable = (...actions) => actions.find((action) => available.includes(action))
  const readAction = firstAvailable('READ', 'view')
  const editAction = firstAvailable('READ_WRITE', 'edit')
  const downloadAction = firstAvailable('DOWNLOADS', 'download')

  switch (preset) {
    case 'all':
      return [...available]
    case 'view':
      return readAction ? [readAction] : []
    case 'download':
      return [readAction, downloadAction].filter(Boolean)
    case 'edit':
      return [readAction, downloadAction, editAction].filter(Boolean)
    case 'clear':
      return []
    default:
      return []
  }
}

const ACTION_ALIASES = {
  view: 'READ',
  download: 'DOWNLOADS',
  edit: 'READ_WRITE'
}

function normalizedActionSet(actions = []) {
  return [...new Set((actions || []).map((action) => ACTION_ALIASES[action] || action))].sort()
}

export function samePermissionActions(left = [], right = []) {
  return normalizedActionSet(left).join('|') === normalizedActionSet(right).join('|')
}

export function includesPermissionActions(selected = [], required = []) {
  const selectedSet = new Set(normalizedActionSet(selected))
  return normalizedActionSet(required).every((action) => selectedSet.has(action))
}

export function togglePresetForScope(scopeActions, preset, currentActions = []) {
  const nextActions = applyPresetToScope(scopeActions, preset)
  return samePermissionActions(currentActions, nextActions) ? [] : nextActions
}

export function scopesForModule(module) {
  return module?.scopes || []
}

export function getScopeParentKey(scope) {
  return scope?.parent_scope || null
}

export function scopeDisplayLabel(scope, parentScope) {
  if (!parentScope) return scope.label

  const dash = ' - '
  if (scope.label.includes(dash)) {
    return scope.label.split(dash).slice(1).join(dash).trim()
  }

  return scope.label
}

export function groupScopesForModule(module) {
  const scopes = scopesForModule(module)
  const groupsByKey = new Map()

  const ensureGroup = (parentKey) => {
    if (!groupsByKey.has(parentKey)) {
      const parentScope = scopes.find((scope) => scope.key === parentKey) || null

      groupsByKey.set(parentKey, {
        key: parentKey,
        label: parentScope?.label || parentKey,
        parentScope,
        children: []
      })
    }

    return groupsByKey.get(parentKey)
  }

  scopes.forEach((scope) => {
    const parentKey = getScopeParentKey(scope)
    if (!parentKey) {
      ensureGroup(scope.key)
      const group = groupsByKey.get(scope.key)
      if (!group.parentScope) {
        group.parentScope = scope
        group.label = scope.label
      }
      return
    }

    const group = ensureGroup(parentKey)
    group.children.push(scope)
    if (!group.label || group.label === parentKey) {
      const parentScope = scopes.find((item) => item.key === parentKey)
      if (parentScope) group.label = parentScope.label
    }
  })

  const groups = [...groupsByKey.values()]
  const order = new Map(scopes.map((scope, index) => [scope.key, index]))

  groups.sort((a, b) => {
    const aOrder = order.get(a.parentScope?.key || a.key) ?? order.get(a.children[0]?.key) ?? 999
    const bOrder = order.get(b.parentScope?.key || b.key) ?? order.get(b.children[0]?.key) ?? 999
    return aOrder - bOrder
  })

  return groups
}

export function applyPermissionDependencies(permissionsByScope, catalog) {
  const next = Object.fromEntries(
    Object.entries(permissionsByScope || {}).map(([key, actions]) => [key, [...(actions || [])]])
  )
  let changed = true

  while (changed) {
    changed = false
    ;(catalog?.modules || []).forEach((module) => {
      ;(module.scopes || []).forEach((scope) => {
        if (!scope.parent_scope) return

        const childActions = next[`${module.key}|${scope.key}`] || []
        if (childActions.length === 0) return

        const parentKey = `${module.key}|${scope.parent_scope}`
        const parentActions = next[parentKey] || []
        if (parentActions.includes('READ')) return

        next[parentKey] = [...parentActions, 'READ']
        changed = true
      })
    })
  }

  return next
}

export function groupHasPermissions(group, moduleKey, permissionsByScope) {
  const keys = []
  if (group.parentScope) keys.push(`${moduleKey}|${group.parentScope.key}`)
  group.children.forEach((scope) => keys.push(`${moduleKey}|${scope.key}`))
  return keys.some((key) => (permissionsByScope[key] || []).length > 0)
}
